import React, { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { VirtuosoGrid } from 'react-virtuoso';
// Api
import { GET_EXPERIENCES } from 'api/experiences/queries';
// Types
import {
  GetExperiences,
  GetExperiencesVariables,
  GetExperiences_getExperiences_entities,
} from 'api/experiences/types/GetExperiences';
import { ExperienceStatus, StoreStatus } from 'api/graphql-global-types';
import { SearchLocation } from 'api/graphql-analytics-global-types';
// Context
import { useFiltersContext } from 'components/common3/PageWithFiltersWrapper/PageWithFiltersContext';
// Helpers
import { getExperienceHoverImage } from 'helpers/experience';
// Ui
import InfiniteScrollLoader from 'ui3/InfiniteScrollLoader/InfiniteScrollLoader';
// Components
import NotFound from 'components/common/NotFound/NotFound';
import ExperienceListItem from 'components/common3/ExperienceList/components/ExperienceListItem/ExperienceListItem';
import PageWithFiltersWrapper from 'components/common3/PageWithFiltersWrapper/PageWithFiltersWrapper';
// Styles
import styles from './AllExperiences.module.scss';

const PER_PAGE = 18;

const VirtuosoGridItem: React.FC<{
  item: GetExperiences_getExperiences_entities;
}> = React.memo(({ item }) => {
  const hoverImageUrl = getExperienceHoverImage(item);
  return (
    <ExperienceListItem
      experience={item}
      type="list"
      priorityImageLoading
      showBuyNowButton
      hoverImageUrl={hoverImageUrl}
    />
  );
});
VirtuosoGridItem.displayName = 'VirtuosoGridItem';

type AllExperienceProps = {
  isPage?: boolean;
  noClear?: boolean;
  storeSlug?: string | undefined | null;
  singleCategory?: boolean;
};

export const AllExperiences = ({
  isPage = true,
  noClear = false,
  storeSlug,
  singleCategory = false,
}: AllExperienceProps) => {
  const { filters, sorting, searchKey, search } = useFiltersContext();

  const inputVariables = useMemo(
    () => ({
      orderBy: sorting.order,
      direction: sorting.direction,
      limit: PER_PAGE,
      offset: 0,
      storeStatuses: [StoreStatus.Active],
      storeIds: [
        ...filters.athleteIds,
        ...filters.organizationIds,
        ...filters.contentCreatorIds,
      ],
      ...{ storeSlug },
      statuses: [ExperienceStatus.Active],
      ...(filters?.sports?.length
        ? { sports: filters.sports.map((i) => Number(i)) }
        : {}),
      ...(filters?.priceRange?.length
        ? {
            price: {
              from: Number(filters?.priceRange[0]),
              to: Number(filters?.priceRange[1]),
            },
          }
        : {}),
      ...(searchKey &&
        search[searchKey] && {
          [searchKey]: search[searchKey],
        }),
    }),
    [filters, sorting, searchKey, search, storeSlug]
  );

  const { data, loading, fetchMore } = useQuery<
    GetExperiences,
    GetExperiencesVariables
  >(GET_EXPERIENCES, {
    variables: {
      input: { ...inputVariables },
    },
  });

  const fetchMoreData = () => {
    const prevOffset = data?.getExperiences?.offset || 0;
    const offset = prevOffset ? prevOffset + PER_PAGE : PER_PAGE;

    fetchMore({
      variables: {
        input: {
          ...inputVariables,
          offset,
        },
      },
    });
  };

  const handleOnEndReached = () => {
    if (hasMore) {
      fetchMoreData();
    }
  };

  const experiences = data?.getExperiences?.entities || [];
  const total = data?.getExperiences?.total;
  const hasMore = data ? experiences.length !== total : false;
  const isNotFound = !loading && total === 0;

  return (
    <PageWithFiltersWrapper
      sortKey="Experiences"
      title="Experiences"
      loading={loading}
      filters={data?.getExperiences?.filters}
      isPage={isPage}
      noClear={noClear}
      searchBarLocation={SearchLocation.Experiences}
      singleCategory={singleCategory}
      headerSpaceForMobile={false}
    >
      {isNotFound && <NotFound title="Experiences not found" />}

      {!!experiences.length && (
        <VirtuosoGrid
          useWindowScroll
          overscan={PER_PAGE * 5}
          data={experiences}
          listClassName={styles.root}
          totalCount={data?.getExperiences.total}
          itemContent={(index, item) => <VirtuosoGridItem item={item} />}
          endReached={handleOnEndReached}
          components={{
            Footer: () => {
              if (!hasMore) {
                return null;
              }

              return <InfiniteScrollLoader />;
            },
          }}
        />
      )}
    </PageWithFiltersWrapper>
  );
};

export default AllExperiences;
